import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { CodedValueDomain } from '../models/attribute-config.model';

type Primitive = string | number | boolean;

export class CommonUtil {

  static clone(object: any, deep = true): any {
    if (deep) {
      // deep copy
      return _.cloneDeep(object);
    }
    // shallow copy
    return _.clone(object);
  }

  static isDeepEqual(oldValue?: object | null, newValue?: object | null): boolean {
    return _.isEqual(oldValue, newValue);
  }

  static isArrayEqual(oldValue: any[], newValue: any[]): boolean {
    if (oldValue == null || newValue == null) {
      return oldValue === newValue;
    }
    if (oldValue.length !== newValue.length) {
      return false;
    }
    const oldValueSorted = oldValue.slice().sort();
    return newValue
      .slice()
      .sort()
      .every((value, index) => {
        return value === oldValueSorted[index];
      });
  }

  static getValueByPath(model: any, path: string): any {
    // TODO: chage to recursive method / handle arrays ?
    let val = model;
    path.split('.').forEach(p => {
      val = val[p];
      if (val === undefined || val == null) {
        return val;
      }
    });

    return val;
  }

  static setValueByPath(value: any, model: any, path: string): any {
    // TODO: chage to recursive method / handle arrays ?
    let val = model;
    const pathArray = path.split('.');
    const fieldName = pathArray.pop();
    pathArray.forEach(p => {
      if (val[p] === undefined || val[p] == null) {
        // create path if doesn't exist
        val[p] = {};
      }

      val = val[p];
    });

    val[fieldName] = value;
  }

  static hasValue(attribute: any) {
    return attribute !== null && attribute !== '' && attribute !== undefined;
  }

  static trimNr(str: string, c: string): string {
    if (str) {
      for (let i = 0; i < str.length; i++) {
        if (str.charAt(i) !== c) {
          return str.substring(i);
        }
      }
    }
    return str;
  }

  static compareDomainName(a: CodedValueDomain, b: CodedValueDomain): number {
    // ignores case, works with umlauts
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  }

  static compareDomainCode(a: CodedValueDomain, b: CodedValueDomain): number {
    if (a.code < b.code) {
      return -1;
    }
    if (a.code > b.code) {
      return 1;
    }
    return 0;
  }

  static getDomainName(domain: CodedValueDomain[], code: number): string {
    if (!this.hasValue(code) || !domain) {
      return '';
    }
    const domainValue = domain.find(x => x.code === code);
    return domainValue?.name;
  }

  static contains(list: any[], value: any): boolean {
    return list.indexOf(value) !== -1;
  }

  static sortByFeatureAttribute(attributeName: string, desc = false): (a: any, b: any) => number {
    const greaterValue = desc ? -1 : 1;
    const lowerValue = greaterValue * -1;

    return (a: any, b: any) => {
      return a.feature.attributes[attributeName] > b.feature.attributes[attributeName]
        ? greaterValue
        : lowerValue;
    };
  }

  static noValue(val: any) {
    return val === undefined || val === null || val.toString().trim().length === 0;
  }

  static isTrue(val: any): boolean {
    if (val === null || val === undefined) {
      return false;
    } else if (isNaN(val)) {
      return val.toLowerCase() === 'true';
    } else {
      return val === true || val === 1 || val === -1;
    }
  }

  static add(...summand: number[]): number {
    return summand.reduce((sum, current) => sum + this.getSummand(current), 0);
  }

  static getSummand(value: number): number {
    return isNaN(value) || value === null ? 0 : value;
  }

  static average(nums: number[]): number {
    return nums.length ? nums.reduce((a, b) => a + b) / nums.length : 0;
  }

  static sortCodeValueNames(codeValues: CodedValueDomain[]) {
    if (codeValues) {
      codeValues.sort((a, b) => (a.name > b.name ? 1 : -1));
    }
  }

  static translateCodeValueNames(
    domainName: string,
    codeValues: CodedValueDomain[],
    translateService: TranslateService
  ) {
    codeValues.forEach(codeValue => {
      CommonUtil.translateCodeValueName(domainName, codeValue, translateService);
    });
  }

  static translateCodeValueName(
    domainName: string,
    codeValue: CodedValueDomain,
    translateService: TranslateService
  ) {
    codeValue.name = translateService.instant(`domain.${domainName}.${codeValue.code}`);
  }

  static distinct<T extends Primitive>(values: T[]): T[] {
    return [...new Set(values)];
  }

  static convertEsriError(error): string {
    if (error.details && error.details.length > 0) {
      let errorString = '';
      for (let j = 0; j < error.details.length; j++) {
        errorString += error.details[j] + ' ';
      }
      return errorString;
    }
    return error.message;
  }

  static immutableRemoveProperty(object: any, property): any {
    const { [property]: deleted, ...reducedObject } = object;
    return reducedObject;
  }

  static calculateDifference(v1, v2): number {
    if (v1 === 0 && v2 === 0) return 0;
    return Math.abs(v1 / v2 - 1);
  }

  static flatArray(arr: any[]): any[] {
    return arr.reduce((acc, val) => acc.concat(val), []);
  }

  static isEqualNumber(a: number, b: number, precision: number): boolean {
    if (isNaN(a) || isNaN(b)) {
      return false;
    }
    if ((a === null || a === undefined) && b !== null && b !== undefined) {
      return false;
    }

    if ((b === null || b === undefined) && a !== null && a !== undefined) {
      return false;
    }
    return a.toFixed(precision) === b.toFixed(precision);
  }
}
